import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { Typography, Box, Stack, Container, CircularProgress, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import AccountSidebar from '../components/AccountSidebar';
import authStore from '../stores/AuthStore';
import registrationStore from '../stores/RegistrationStore';
import { useTheme } from '@mui/material/styles';

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString();
};

const DetailLine = ({ label, value }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <Typography variant="body2" color="text.secondary">
      <Box component="span" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
        {label}:
      </Box>{' '}
      {value}
    </Typography>
  );
};

const RegisteredDevicesPage = observer(() => {
  const theme = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authStore.loading && !authStore.userId) {
      navigate('/sign-in');
      return;
    }
    if (authStore.userId) {
      registrationStore.fetchMine();
    }
  }, [navigate]);

  if (authStore.loading || !authStore.userId) {
    return (
      <PageLayout pageTitle="Loading...">
        <CircularProgress />
      </PageLayout>
    );
  }

  const { registrations, loading, error } = registrationStore;

  return (
    <PageLayout pageTitle="Registered Devices">
      <Box sx={{ display: 'flex', minHeight: '100vh', mt: 4, bgcolor: theme.palette.background.default }}>
        <AccountSidebar />
        <Container sx={{ m: 4, flex: 1 }}>
          <Typography variant="h4" gutterBottom>
            Registered Devices
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <CircularProgress />
          ) : registrations.length === 0 ? (
            <Typography color="text.secondary">
              You have no registered devices yet. Devices are registered from the Ear Physics mobile app.
              Once you register a unit there, it will appear here.
            </Typography>
          ) : (
            <Stack spacing={2}>
              {registrations.map((registration) => {
                const deviceName =
                  registration.name ||
                  (registration.product && registration.product.name) ||
                  registration.serial ||
                  'Ear Physics device';

                const registeredDate = formatDate(registration.registeredAt);
                const lastSeenDate = formatDate(registration.lastSeenAt);

                return (
                  <Box
                    key={registration._id}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 2,
                      bgcolor: theme.palette.background.paper,
                    }}
                  >
                    <Stack spacing={0.5}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
                        {deviceName}
                      </Typography>
                      <DetailLine label="Firmware version" value={registration.firmwareVersion} />
                      <DetailLine label="DSP version" value={registration.dspVersion} />
                      <DetailLine label="Serial" value={registration.serial} />
                      <DetailLine
                        label="Order #"
                        value={registration.order && registration.order.orderNumber}
                      />
                      <DetailLine label="Registered" value={registeredDate} />
                      <DetailLine label="Last seen" value={lastSeenDate} />
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Container>
      </Box>
    </PageLayout>
  );
});

export default RegisteredDevicesPage;
